import React, { useMemo } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { Button, CircularProgress, Grid } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import moment from "moment";
import { get, isEmpty, omit } from "lodash";

import Backdrop from "Components/Common/Backdrop";
import Modal from "Components/Common/Modal";
import DatePicker from "Components/Common/DatePicker";
import InputFieldText from "Components/Common/InputFieldText";
import PaymentRequest from "Components/PaymentRequest";

import { convertCamelTextToCapitalizeText } from "utils/convertCamelTextToCapitalizeText";
import { dateFormatter } from "utils/dateFormatter";
import { onlyNumberKeys } from "utils";

import {
  useGetDemandEstimationByApplicationIdQuery,
  usePostDemandEstimationPaymentMutation,
} from "apis/DemandEstimation";

const DemandEstimatePaymentModal = ({
  applicationId,
  handleClose,
  handleCloseForSubmit,
  isPowerUtility,
  t,
}) => {
  const [getEncryptionForPayment, { isLoading }] =
    usePostDemandEstimationPaymentMutation();

  const {
    data: { data: { id, charges } = {} } = {},
    isLoading: isDemandEstimateDataLoading,
  } = useGetDemandEstimationByApplicationIdQuery(applicationId);

  const initialValues = useMemo(
    () =>
      charges
        ? Object.entries(charges).reduce(
            (acc, [key, value]) => {
              if (value > 0) {
                acc[key] = value;
                acc[`${key}Tds`] = 0;
                if (!isPowerUtility) {
                  acc[`${key}TdsYear`] = moment();
                }
              }
              return acc;
            },
            { ...(isPowerUtility && { tdsYear: moment() }) }
          )
        : { tdsYear: moment() },
    [charges]
  );

  const validationSchema = useMemo(
    () =>
      charges
        ? Object.entries(charges).reduce(
            (acc, [key, value]) => {
              if (value > 0) {
                const capitalizeKey = convertCamelTextToCapitalizeText(key);

                acc[`${key}Tds`] = Yup.number()
                  .required(`${capitalizeKey} is required`)
                  .test(`isValid${key}TDSAmount`, "", function () {
                    const [{ value: current = null } = {}] = this.from || [];

                    if (current[`${key}Tds`] < 0) {
                      return this.createError({
                        message: `${capitalizeKey} TDS Amount must greater than 0`,
                      });
                    }

                    if (current[key] < current[`${key}Tds`]) {
                      return this.createError({
                        message: `${capitalizeKey} TDS Amount must be less than ${capitalizeKey}`,
                      });
                    }

                    return true;
                  });

                if (!isPowerUtility) {
                  acc[`${key}TdsYear`] = Yup.date().test(
                    "isValidYear",
                    "",
                    function () {
                      const [{ value: current = {} } = {}] = this.from || [];
                      if (!current[`${key}TdsYear`]) {
                        return this.createError({
                          message: "Select financial year",
                        });
                      }
                      return true;
                    }
                  );
                }
              }
              return acc;
            },
            {
              ...(isPowerUtility && {
                tdsYear: Yup.date().test("isValidYear", "", function () {
                  const [{ value: current = {} } = {}] = this.from || [];
                  if (!current.tdsYear) {
                    return this.createError({
                      message: "Select financial year",
                    });
                  }
                  return true;
                }),
              }),
            }
          )
        : {},
    [charges]
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: Yup.object(validationSchema),
  });

  const {
    values,
    errors,
    values: { tdsYear },
  } = formik;

  const payNowBtnHandler = async () => {
    try {
      if (isEmpty(errors)) {
        const payload = omit(
          {
            id,
            ...values,
          },
          [...Object.keys(charges), "tdsYear"]
        );

        Object.entries(charges).forEach(([key, value]) => {
          if (value > 0) {
            payload[`${key}TdsYear`] = isPowerUtility
              ? tdsYear.format("YYYY")
              : get(values, `${key}TdsYear`).format("YYYY");
          }
        });
        return await getEncryptionForPayment(payload);
      }
    } catch (error) {}
  };

  if (isDemandEstimateDataLoading) {
    return <Backdrop open />;
  }

  return (
    <Modal
      isOpen
      handleClose={handleClose}
      title={t("Demand Estimate Payment")}
      maxWidth="md"
    >
      <Grid
        container
        rowSpacing={1}
        columnSpacing={{ xs: 1, sm: 2, md: 3 }}
        className="mt-3"
      >
        {isPowerUtility && (
          <Grid item xs={12}>
            <DatePicker
              name="tdsYear"
              formik={formik}
              label={t("Financial Year*")}
              views={["year"]}
              format={dateFormatter(tdsYear)}
            />
          </Grid>
        )}
        {!!charges &&
          Object.entries(charges).map(([key, value]) => (
            <>
              {value > 0 && (
                <>
                  <Grid item xs={12} sm={6} md={isPowerUtility ? 6 : 4}>
                    <InputFieldText
                      label={`${convertCamelTextToCapitalizeText(key)}*`}
                      type="number"
                      InputProps={{
                        inputProps: {
                          min: 0,
                          onKeyDown: (e) => onlyNumberKeys(e, true),
                        },
                      }}
                      formik={formik}
                      name={key}
                      placeholder={`Enter ${convertCamelTextToCapitalizeText(
                        key
                      )}`}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={isPowerUtility ? 6 : 4}>
                    <InputFieldText
                      label={`${convertCamelTextToCapitalizeText(
                        key
                      )} TDS Amount*`}
                      type="number"
                      InputProps={{
                        inputProps: {
                          min: 0,
                          onKeyDown: (e) => onlyNumberKeys(e, true),
                        },
                      }}
                      formik={formik}
                      name={`${key}Tds`}
                      placeholder={`Enter ${convertCamelTextToCapitalizeText(
                        key
                      )} TDS Amount`}
                    />
                  </Grid>

                  {!isPowerUtility && (
                    <Grid item xs={12} sm={6} md={4}>
                      <DatePicker
                        name={`${key}TdsYear`}
                        formik={formik}
                        label={`${convertCamelTextToCapitalizeText(
                          key
                        )} Financial Year*`}
                        views={["year"]}
                        format={dateFormatter(get(values, `${key}TdsYear`))}
                      />
                    </Grid>
                  )}
                </>
              )}
            </>
          ))}
        <Grid item xs={12} className="text-end mt-3">
          <PaymentRequest
            handleClose={() => {
              handleClose();
              handleCloseForSubmit();
            }}
            generateEncryption={payNowBtnHandler}
          >
            <Button disabled={isLoading} variant="contained">
              {t("Pay Now")} {isLoading && <CircularProgress size={20} />}
            </Button>
          </PaymentRequest>
        </Grid>
      </Grid>
    </Modal>
  );
};

DemandEstimatePaymentModal.propTypes = {
  applicationId: PropTypes.number,
  handleClose: PropTypes.func,
  handleCloseForSubmit: PropTypes.func,
  isPowerUtility: PropTypes.bool,
  t: PropTypes.func,
};

export default withTranslation()(DemandEstimatePaymentModal);
